import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, TrendingUp, AlertTriangle, Zap } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  CartesianGrid,
} from "recharts";
import { api } from "@/lib/api";
import type { ProductInsight } from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";

const TAG_STYLES: Record<string, { color: string; label: string; hint: string }> = {
  high_buyer_intent: { color: "violet", label: "High buyer intent", hint: "Convert now" },
  hidden_winner: { color: "green", label: "Hidden winner", hint: "Low reach, high intent" },
  viral_low_conversion: { color: "amber", label: "Viral, low conversion", hint: "Fix the funnel" },
  complaint_heavy: { color: "red", label: "Complaint heavy", hint: "Investigate quality" },
};

const OpportunityBadge: React.FC<{ label: "low" | "medium" | "high"; score: number }> = ({
  label,
  score,
}) => {
  const colors = {
    low: "bg-slate-100 text-slate-700 border-slate-200",
    medium: "bg-amber-50 text-amber-800 border-amber-200",
    high: "bg-violet-50 text-violet-700 border-violet-200",
  }[label];
  return (
    <div className={`sg-chip ${colors} font-semibold`}>
      {label.toUpperCase()} · {(score * 100).toFixed(0)}
    </div>
  );
};

export const Insights: React.FC = () => {
  const products = useQuery<{ items: ProductInsight[] }>({
    queryKey: ["insight-products"],
    queryFn: async () => (await api.get("/insights/products")).data,
  });
  const hashtags = useQuery<{ items: { tag: string; count: number }[] }>({
    queryKey: ["insight-hashtags"],
    queryFn: async () => (await api.get("/insights/hashtags")).data,
  });
  const timeline = useQuery<{ items: any[] }>({
    queryKey: ["intent-timeline"],
    queryFn: async () => (await api.get("/insights/intent-timeline?days=14")).data,
  });

  const items = products.data?.items ?? [];
  const hasData = items.some((i) => i.volume > 0);

  const barData = items
    .filter((i) => i.volume > 0)
    .slice(0, 8)
    .map((i) => ({
      name: i.title.length > 18 ? i.title.slice(0, 16) + "…" : i.title,
      score: Math.round(i.opportunity_score * 100),
      volume: i.volume,
    }));

  const timelineData = (timeline.data?.items ?? []).map((d) => ({
    date: d.date.slice(5),
    buying: d.buying_intent,
    question: d.product_question,
    complaint: d.complaint,
    neutral: d.neutral,
  }));

  return (
    <DashboardLayout
      title="Insights"
      subtitle="Explainable scoring across your products, hashtags and audience signals."
      seoPath="/insights"
      seoDescription="Explainable scoring across your products, hashtags and audience signals — the SociBaba insights workspace."
    >
      {products.isLoading ? (
        <div className="sg-skeleton h-96" />
      ) : hasData ? (
        <div className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="sg-card p-6 lg:col-span-2" data-testid="opportunity-chart">
              <div className="sg-label mb-1">Opportunity score by product</div>
              <div className="font-display text-lg font-semibold tracking-tight mb-4">
                Where should you double down?
              </div>
              <div className="h-72" style={{ minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                  <BarChart data={barData} margin={{ top: 8, right: 8, left: -8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" fontSize={11} tick={{ fill: "#64748b" }} />
                    <YAxis fontSize={11} tick={{ fill: "#64748b" }} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="score" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="sg-card p-6" data-testid="hashtag-panel">
              <div className="sg-label mb-1">Top hashtags</div>
              <div className="font-display text-lg font-semibold tracking-tight mb-4">
                Best performing tags
              </div>
              {hashtags.data && hashtags.data.items.length > 0 ? (
                <ul className="space-y-2">
                  {hashtags.data.items.slice(0, 8).map((h) => (
                    <li key={h.tag} className="flex items-center justify-between">
                      <span className="text-sm font-medium">#{h.tag}</span>
                      <span className="text-xs text-[color:var(--sg-muted)]">{h.count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-[color:var(--sg-muted)]">
                  Add tags to products to see this list.
                </div>
              )}
            </div>
          </div>

          <div className="sg-card p-6" data-testid="intent-timeline">
            <div className="sg-label mb-1">Intent timeline · last 14 days</div>
            <div className="font-display text-lg font-semibold tracking-tight mb-4">
              How audience intent is shifting
            </div>
            <div className="h-72" style={{ minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                <LineChart data={timelineData} margin={{ top: 8, right: 8, left: -8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" fontSize={11} tick={{ fill: "#64748b" }} />
                  <YAxis fontSize={11} tick={{ fill: "#64748b" }} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="buying" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="question" stroke="#2563eb" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="complaint" stroke="#dc2626" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="neutral" stroke="#64748b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="sg-card p-6" data-testid="product-insights-table">
            <div className="sg-label mb-1">Product intelligence</div>
            <div className="font-display text-lg font-semibold tracking-tight mb-4">
              Explainable opportunity scoring
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                    <th className="pb-3">Product</th>
                    <th className="pb-3">Volume</th>
                    <th className="pb-3">Buying %</th>
                    <th className="pb-3">Complaint %</th>
                    <th className="pb-3">Opportunity</th>
                    <th className="pb-3">Signals</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.product_id} className="border-b border-[color:var(--sg-border)] last:border-b-0">
                      <td className="py-3 font-medium">{p.title}</td>
                      <td className="py-3">{p.volume}</td>
                      <td className="py-3">{(p.buying_ratio * 100).toFixed(0)}%</td>
                      <td className="py-3">{(p.complaint_ratio * 100).toFixed(0)}%</td>
                      <td className="py-3">
                        <OpportunityBadge label={p.opportunity_label} score={p.opportunity_score} />
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {p.tags.map((t) => {
                            const cfg = TAG_STYLES[t];
                            if (!cfg) return null;
                            const cls = {
                              violet: "bg-violet-50 text-violet-700 border-violet-200",
                              green: "bg-emerald-50 text-emerald-700 border-emerald-200",
                              amber: "bg-amber-50 text-amber-800 border-amber-200",
                              red: "bg-rose-50 text-rose-700 border-rose-200",
                            }[cfg.color] || "";
                            const Icon = {
                              violet: Sparkles,
                              green: Zap,
                              amber: TrendingUp,
                              red: AlertTriangle,
                            }[cfg.color] || Sparkles;
                            return (
                              <span key={t} title={cfg.hint} className={`sg-chip ${cls}`}>
                                <Icon className="h-3 w-3" /> {cfg.label}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <EmptyState
          title="Not enough data yet"
          description="Publish content to populate insights."
        />
      )}
    </DashboardLayout>
  );
};
